import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { getRarityColor, getRarityLabel, getRarityGlow } from '../utils/rarityUtils';
import ItemTooltipContent from './shared/ItemTooltipContent';
import './InventoryOverflowModal.css';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

/**
 * InventoryOverflowModal — Forces the player to discard items when inventory
 * exceeds capacity. Shows all items with NEW badges on recently acquired ones.
 * Cannot be dismissed until inventory is at or under capacity.
 */
const InventoryOverflowModal = ({ inventory, maxCapacity, onResolved }) => {
  const [selectedIndices, setSelectedIndices] = useState(new Set());
  const [discarding, setDiscarding] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [tooltipItem, setTooltipItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);

  // Adjust tooltip if it overflows the viewport
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || !tooltipItem) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    if (rect.bottom > viewportH - 8) {
      el.style.top = `${Math.max(8, viewportH - rect.height - 8)}px`;
    }
  });

  const handleMouseEnter = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipWidth = 260;
    const x = Math.min(rect.right + 8, window.innerWidth - tooltipWidth - 12);
    setTooltipItem(item);
    setTooltipPos({ x: Math.max(8, x), y: rect.top });
  };
  const handleMouseLeave = () => setTooltipItem(null);

  const overflowCount = inventory.length - maxCapacity;
  const remainingToDiscard = overflowCount - selectedIndices.size;

  const toggleSelect = useCallback((index) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
    setError(null);
  }, []);

  const handleConfirmDiscard = useCallback(async () => {
    if (remainingToDiscard > 0 || discarding) return;

    setDiscarding(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/player/discard-items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // Send {index, name} pairs so the server can match by name when
          // positional indices are stale (e.g. after a background consolidation).
          discardItems: Array.from(selectedIndices).map(i => ({
            index: i,
            name: inventory[i]?.name,
          })),
        })
      });

      const data = await response.json();

      if (data.success) {
        setConfirmed(true);
        onResolved();
      } else {
        setError(data.error || 'Failed to discard items');
      }
    } catch (err) {
      console.error('Error discarding items:', err);
      setError('Network error — try again');
    } finally {
      setDiscarding(false);
    }
  }, [selectedIndices, remainingToDiscard, discarding, onResolved]);

  // Helper: get a short stat summary for an item
  const getItemSummary = (item) => {
    const parts = [];
    if (item.damage) parts.push(`⚔️${item.damage}`);
    if (item.att) parts.push(`🗡️+${item.att}`);
    if (item.bonus) parts.push(`🛡️+${item.bonus}`);
    if (item.hp) parts.push(`❤️+${item.hp}`);
    if (item.mp) parts.push(`💧+${item.mp}`);
    if (item.size) parts.push(`🎒+${item.size}`);
    if (item.spell) parts.push(`📖 ${item.spell}`);
    return parts.join('  ');
  };

  // Type icon mapping
  const getTypeIcon = (type) => {
    const icons = {
      weapon: '⚔️', armor: '🛡️', breastplate: '🛡️', helm: '🪖',
      gloves: '🧤', grieves: '🥾', pauldrons: '🦺', belt: '📿',
      ring: '💍', shield: '🛡️', backpack: '🎒', parts: '🔧',
      gem: '💎', restore: '🧪', scroll: '📜', recipe: '📋',
      food: '🍖', key: '🔑', misc: '📦',
    };
    return icons[type] || '📦';
  };

  // Show a holding screen after a successful discard while the parent component
  // waits for refreshed character data before closing the modal.
  // This prevents the stale-looking flash that would occur if we simply waited.
  if (confirmed) {
    return (
      <div className="overflow-modal-backdrop">
        <div className="overflow-modal overflow-modal-confirmed">
          <div className="overflow-confirmed-icon">✓</div>
          <div className="overflow-confirmed-msg">Items discarded!</div>
          <div className="overflow-confirmed-sub">Updating inventory…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-modal-backdrop">
      <div className="overflow-modal">
        <div className="overflow-modal-header">
          <h2>⚠️ Inventory Full</h2>
          <div className="overflow-modal-subtitle">
            Choose items to discard to make room
          </div>
        </div>

        <div className="overflow-modal-counter">
          <span className="overflow-count-current">{inventory.length - selectedIndices.size}</span>
          <span className="overflow-count-sep"> / </span>
          <span className="overflow-count-max">{maxCapacity}</span>
          <span className="overflow-count-label"> slots</span>
          {remainingToDiscard > 0 && (
            <span className="overflow-remaining">
              — select {remainingToDiscard} more to discard
            </span>
          )}
          {remainingToDiscard <= 0 && (
            <span className="overflow-ready">
              ✓ Ready to confirm
            </span>
          )}
        </div>

        <div className="overflow-modal-items">
          {inventory.map((item, index) => {
            const isNew = item._new;
            const isSelected = selectedIndices.has(index);
            const rarityColor = getRarityColor(item);
            const rarityLabel = getRarityLabel(item);
            const rarityGlow = getRarityGlow(item);
            const summary = getItemSummary(item);

            return (
              <div
                key={index}
                className={`overflow-item ${isSelected ? 'overflow-item-selected' : ''} ${isNew ? 'overflow-item-new' : ''}`}
                onClick={() => toggleSelect(index)}
                onMouseEnter={(e) => handleMouseEnter(e, item)}
                onMouseLeave={handleMouseLeave}
                title={isSelected ? 'Click to keep this item' : 'Click to mark for discard'}
              >
                {/* NEW badge */}
                {isNew && !isSelected && (
                  <span className="overflow-new-badge">NEW</span>
                )}

                {/* Discard X badge */}
                {isSelected && (
                  <span className="overflow-discard-badge">✕</span>
                )}

                {/* Item icon */}
                <span className="overflow-item-icon">{getTypeIcon(item.type)}</span>

                {/* Item info */}
                <div className="overflow-item-info">
                  <div
                    className="overflow-item-name"
                    style={{ color: rarityColor, textShadow: rarityGlow }}
                  >
                    {item.name}
                    {(item.quantity || 1) > 1 && (
                      <span className="overflow-item-qty"> x{item.quantity}</span>
                    )}
                  </div>
                  <div className="overflow-item-meta">
                    <span className="overflow-item-rarity" style={{ color: rarityColor }}>
                      {rarityLabel}
                    </span>
                    <span className="overflow-item-type">{item.type}</span>
                    {summary && <span className="overflow-item-stats">{summary}</span>}
                  </div>
                </div>

                {/* Value */}
                <div className="overflow-item-value">
                  {item.cost || 0}g
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="overflow-modal-error">{error}</div>
        )}

        <div className="overflow-modal-footer">
          <div className="overflow-modal-hint">
            Click items to select them for discard. New loot is marked with a <span className="overflow-hint-new">NEW</span> badge.
          </div>
          <button
            className={`overflow-confirm-btn ${remainingToDiscard <= 0 ? 'overflow-confirm-ready' : 'overflow-confirm-disabled'}`}
            onClick={handleConfirmDiscard}
            disabled={remainingToDiscard > 0 || discarding}
          >
            {discarding
              ? '⏳ Discarding...'
              : remainingToDiscard > 0
                ? `Select ${remainingToDiscard} more item(s)`
                : `🗑️ Discard ${selectedIndices.size} Item(s)`}
          </button>
        </div>
      </div>

      {/* Item Tooltip */}
      {tooltipItem && (
        <div
          className="item-tooltip-popup"
          ref={tooltipRef}
          style={{ position: 'fixed', left: tooltipPos.x, top: tooltipPos.y, zIndex: 10001 }}
        >
          <ItemTooltipContent item={tooltipItem} compact />
        </div>
      )}
    </div>
  );
};

export default InventoryOverflowModal;
